import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { CheckCircle } from 'lucide-react'
import { usePageMeta } from '@/hooks/usePageMeta'
import { publicApi } from '../../lib/api'
import { FALLBACK_FEE_SCHEDULE, formatPercent } from '../../lib/fees'

// Keep only finite numbers from the API; anything else falls back to the static schedule.
function mergeFeeSchedule(payload) {
  const merged = { ...FALLBACK_FEE_SCHEDULE }
  if (payload && typeof payload === 'object') {
    for (const key of Object.keys(FALLBACK_FEE_SCHEDULE)) {
      if (typeof payload[key] === 'number' && Number.isFinite(payload[key])) {
        merged[key] = payload[key]
      }
    }
  }
  return merged
}

export default function Pricing() {
  const navigate = useNavigate()
  const [fees, setFees] = useState(FALLBACK_FEE_SCHEDULE)

  useEffect(() => {
    let cancelled = false
    publicApi
      .getFeeSchedule()
      .then((f) => {
        if (!cancelled && f) setFees(mergeFeeSchedule(f))
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [])

  const oneTime = `${formatPercent(fees.oneTimePercent)}%`
  const roundup = `${formatPercent(fees.roundupPercent)}%`
  const fixed = `$${(fees.fixedCents / 100).toFixed(2)}`
  usePageMeta({
    title: 'Pricing | Claritas',
    description:
      `Claritas is free for donors. A transaction fee of ${oneTime} + ${fixed} per standard donation and ${roundup} + ${fixed} per round-up is deducted from each donation—no monthly fees, no subscriptions.`,
  })

  return (
    <div className="min-h-screen bg-background-light">

      {/* Hero */}
      <section className="bg-white py-20 border-b border-surface-border">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xs font-extrabold tracking-widest uppercase text-gold mb-4">
            Pricing
          </p>
          <div className="w-8 h-px bg-gold mx-auto mb-6" />
          <h1 className="text-4xl sm:text-5xl font-bold font-display text-charcoal mb-6">
            Simple, Transparent Pricing
          </h1>
          <p className="text-base text-charcoal-muted max-w-2xl mx-auto">
            Claritas is free for donors. A small transaction fee is deducted from each donation
            received—no subscriptions, no hidden charges.
          </p>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="bg-background-light py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-3xl mx-auto">

            {/* Donor Plan */}
            <div className="bg-white p-8 border border-surface-border rounded shadow-card">
              <p className="text-xs font-extrabold tracking-widest uppercase text-gold mb-4">
                For Donors
              </p>
              <div className="mb-6">
                <span className="text-4xl font-bold font-display text-charcoal">Free</span>
              </div>
              <p className="text-sm text-charcoal-muted mb-8">
                Donors pay exactly the amount they choose. A small transaction fee is deducted from
                each gift before it reaches the organization.
              </p>
              <ul className="space-y-3 mb-8">
                {[
                  'Unlimited round-up donations',
                  'One-time donations anytime',
                  'Giving history and dashboard',
                  'Secure bank linking via Stripe',
                  'Parish directory access',
                  'Full account control',
                ].map(item => (
                  <li key={item} className="flex items-center gap-3 text-sm text-charcoal-muted">
                    <CheckCircle className="w-4 h-4 text-gold flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <Button onClick={() => navigate('/sign-in')} className="w-full bg-gold text-black font-semibold hover:bg-gold/90 shadow-gold">
                Get Started Free
              </Button>
            </div>

            {/* Parish Plan */}
            <div className="bg-white p-8 border-2 border-gold rounded shadow-card relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <span className="bg-gold text-black text-xs font-extrabold tracking-widest uppercase px-3 py-1 rounded-full">
                  For Parishes
                </span>
              </div>
              <p className="text-xs font-extrabold tracking-widest uppercase text-gold mb-4 mt-2">
                Parish Platform
              </p>
              <div className="mb-2 space-y-2">
                <div>
                  <span className="text-4xl font-bold font-display text-charcoal">{oneTime}</span>
                  <span className="text-sm text-charcoal-muted ml-2">{`+ ${fixed} per standard donation`}</span>
                </div>
                <div>
                  <span className="text-4xl font-bold font-display text-charcoal">{roundup}</span>
                  <span className="text-sm text-charcoal-muted ml-2">{`+ ${fixed} per round-up`}</span>
                </div>
              </div>
              <p className="text-xs text-charcoal-faint mb-6">Deducted automatically from each donation before payout</p>
              <p className="text-sm text-charcoal-muted mb-8">
                A transaction fee is deducted only from donations received. No platform subscription required.
              </p>
              <ul className="space-y-3 mb-8">
                {[
                  'Parish admin dashboard',
                  'Real-time donation tracking',
                  'Donor activity reports',
                  'Stripe payment processing',
                  'Parish directory listing',
                  'Community donor management',
                  'Email support',
                ].map(item => (
                  <li key={item} className="flex items-center gap-3 text-sm text-charcoal-muted">
                    <CheckCircle className="w-4 h-4 text-gold flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <Button onClick={() => navigate('/sign-in')} className="w-full bg-gold text-black font-semibold hover:bg-gold/90 shadow-gold">
                Register Your Parish
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-white py-20 border-t border-surface-border">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-xs font-extrabold tracking-widest uppercase text-gold mb-4">
              FAQ
            </p>
            <h2 className="text-3xl font-bold font-display text-charcoal mb-4">
              Common Questions
            </h2>
          </div>

          <div className="space-y-8">
            {[
              {
                q: 'Is Claritas really free for donors?',
                a: 'Yes. Donors pay nothing to use Claritas. A standard payment processing fee is deducted from your round-ups and donations before the remainder reaches your chosen organization.',
              },
              {
                q: 'What are the payment processing fees?',
                a: `A fee of ${oneTime} + ${fixed} is deducted from each standard donation, and ${roundup} + ${fixed} from each round-up transaction. These fees come out of the amount the organization receives—donors are charged exactly what they authorize.`,
              },
              {
                q: 'Are there any monthly subscription fees for parishes?',
                a: 'No. Claritas charges no monthly or annual subscription. The only cost is the transaction fee deducted from actual donations received.',
              },
              {
                q: 'Can I change my round-up settings or cancel anytime?',
                a: 'Absolutely. Donors have full control and can pause, adjust, or disable round-ups at any time from their account dashboard.',
              },
              {
                q: 'How are funds deposited to a parish?',
                a: "Donations are processed through Stripe and deposited directly into the parish's connected bank account on a standard Stripe payout schedule, typically within 2 business days.",
              },
            ].map(({ q, a }) => (
              <div key={q} className="border-b border-surface-border pb-8">
                <h3 className="text-base font-bold font-display text-charcoal mb-2">{q}</h3>
                <p className="text-sm text-charcoal-muted">{a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-background-light py-20 border-t border-surface-border">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xs font-extrabold tracking-widest uppercase text-gold mb-4">
            Get Started
          </p>
          <div className="w-8 h-px bg-gold mx-auto mb-6" />
          <h2 className="text-3xl font-bold font-display text-charcoal mb-4">
            No Cost to Get Started
          </h2>
          <p className="text-base text-charcoal-muted mb-8 max-w-xl mx-auto">
            Create a donor account for free, or register your parish today and begin receiving
            contributions from your community.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Button size="lg" onClick={() => navigate('/sign-in')} className="bg-gold text-black font-semibold hover:bg-gold/90 shadow-gold">
              Get Started
            </Button>
            <Button variant="ghost" size="lg" onClick={() => navigate('/for-parishes')}>
              Learn More
            </Button>
          </div>
        </div>
      </section>
    </div>
  )
}
